import { useState, useEffect } from 'react';
import { getAdministrativeUnitsByLevel } from '../services/administrativeUnitService';
import { organizationService } from '../services/organizationService';
import type { AdministrativeUnit } from '../types/administrativeUnit';
import type { Organization } from '../types/organization';

async function fetchAllData() {
  const [
    level0Units,
    level1Units,
    level2Units,
    organizationsResponse,
    defaultOrgResponse,
  ] = await Promise.all([
    getAdministrativeUnitsByLevel(0),
    getAdministrativeUnitsByLevel(1),
    getAdministrativeUnitsByLevel(2),
    organizationService.getOrganizations({ isActive: true, limit: 50 }),
    organizationService.getOrganizations({ search: 'default' }),
  ]);

  const allAdminUnits = [
    ...(level0Units || []),
    ...(level1Units || []),
    ...(level2Units || []),
  ];

  const regularOrganizations = organizationsResponse.organizations || [];
  const defaultOrganizations = defaultOrgResponse.organizations || [];

  const allOrganizations = [...regularOrganizations];
  defaultOrganizations.forEach((defaultOrg: Organization) => {
    if (!allOrganizations.find(org => org.id === defaultOrg.id)) {
      allOrganizations.push(defaultOrg);
    }
  });

  return { allAdminUnits, provinces: level1Units || [], allOrganizations };
}

export function useProductConfigData() {
  const [adminUnits, setAdminUnits] = useState<AdministrativeUnit[]>([]);
  const [allAdminUnits, setAllAdminUnits] = useState<AdministrativeUnit[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const doFetch = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await fetchAllData();

        setAdminUnits(result.provinces);
        setAllAdminUnits(result.allAdminUnits);
        setOrganizations(result.allOrganizations);
      } catch (err) {
        console.error('Error fetching product config data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load configuration data');
        setAdminUnits([]);
        setAllAdminUnits([]);
        setOrganizations([]);
      } finally {
        setLoading(false);
      }
    };

    doFetch();
  }, []);

  const refetch = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await fetchAllData();

      setAdminUnits(result.provinces);
      setAllAdminUnits(result.allAdminUnits);
      setOrganizations(result.allOrganizations);
    } catch (err) {
      console.error('Error refetching product config data:', err);
      setError(err instanceof Error ? err.message : 'Failed to reload configuration data');
      setAdminUnits([]);
      setAllAdminUnits([]);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  return { 
    adminUnits,
    allAdminUnits,
    organizations, 
    loading, 
    error,
    refetch
  };
}
